// ---------------------------------------------------------------------------
// Rendering text in a system font (Arial, Times, ...) as a ZPL graphic.
//
// The printer only has its own resident fonts; Arial is not among them. The usual
// way round that is to download a TrueType font into printer memory, but that writes
// to the hardware and follows it to every other machine — so instead the text is
// rasterised here and sent as a ^GF bitmap.
//
// What that buys: any installed font, and real italic, which no resident ZPL font
// has. What it costs: the text becomes an image, so it cannot be re-scaled by the
// printer, and the payload is larger than a ^FD string.
// ---------------------------------------------------------------------------

/** Fonts offered for rasterising. Each needs a sane fallback chain. */
export const SYSTEM_FONTS = [
  { key: "Arial", label: "Arial", stack: "Arial, Helvetica, sans-serif" },
  { key: "Helvetica", label: "Helvetica", stack: "Helvetica, Arial, sans-serif" },
  { key: "Verdana", label: "Verdana", stack: "Verdana, Geneva, sans-serif" },
  { key: "Tahoma", label: "Tahoma", stack: "Tahoma, Verdana, sans-serif" },
  { key: "Calibri", label: "Calibri", stack: "Calibri, Candara, sans-serif" },
  { key: "Times New Roman", label: "Times New Roman", stack: "'Times New Roman', Times, serif" },
  { key: "Georgia", label: "Georgia", stack: "Georgia, 'Times New Roman', serif" },
  { key: "Courier New", label: "Courier New", stack: "'Courier New', Courier, monospace" },
  { key: "Impact", label: "Impact", stack: "Impact, Haettenschweiler, sans-serif" },
];

export const systemFontByKey = (key) =>
  SYSTEM_FONTS.find((f) => f.key === key) || SYSTEM_FONTS[0];

/** CSS font shorthand for a text element, used for both measuring and drawing. */
export function cssFontFor(el) {
  const stack = systemFontByKey(el.fontFamily).stack;
  const style = el.italic ? "italic " : "";
  const weight = el.bold ? "700 " : "400 ";
  return `${style}${weight}${el.fontH}px ${stack}`;
}

let ctx = null;
function context2d() {
  if (!ctx) {
    const canvas = document.createElement("canvas");
    ctx = canvas.getContext("2d", { willReadFrequently: true });
  }
  return ctx;
}

/** Width in dots a system-font line needs. Exact — the same canvas draws it. */
export function measureSystemTextDots(text, el) {
  const s = String(text == null ? "" : text);
  if (!s) return 0;
  try {
    const c = context2d();
    c.font = cssFontFor(el);
    return c.measureText(s).width;
  } catch (e) {
    return s.length * el.fontH * 0.6;
  }
}

// Rasterising on every keystroke would be wasteful, and a print run repeats the same
// strings; key the cache on everything that changes the pixels.
const cache = new Map();
const CACHE_LIMIT = 300;

/**
 * Rasterise one or more lines into a ^GF graphic field.
 *
 * @returns {{zpl: string, widthDots: number, heightDots: number}}
 */
export function rasterizeTextToGF(lines, el, opts = {}) {
  const text = lines.join("\n");
  const key = [
    text,
    el.fontFamily,
    el.fontH,
    el.bold ? 1 : 0,
    el.italic ? 1 : 0,
    el.underline ? 1 : 0,
    el.strikethrough ? 1 : 0,
    el.align,
    opts.blockDots,
  ].join("|");
  const hit = cache.get(key);
  if (hit) return hit;

  const c = context2d();
  c.font = cssFontFor(el);

  const lineH = Math.ceil(el.fontH * 1.2);
  const widthDots = Math.max(1, Math.ceil(opts.blockDots || measureSystemTextDots(text, el)));
  const heightDots = Math.max(1, lineH * lines.length);

  const canvas = c.canvas;
  canvas.width = widthDots;
  canvas.height = heightDots;

  // Re-assert after resizing: changing canvas dimensions resets the context.
  c.font = cssFontFor(el);
  c.fillStyle = "#fff";
  c.fillRect(0, 0, widthDots, heightDots);
  c.fillStyle = "#000";
  c.textBaseline = "alphabetic";

  lines.forEach((line, i) => {
    const w = c.measureText(line).width;
    const x = el.align === "C" ? (widthDots - w) / 2 : el.align === "R" ? widthDots - w : 0;
    const baseline = i * lineH + el.fontH;
    c.fillText(line, x, baseline);

    const rule = Math.max(1, Math.round(el.fontH / 12));
    if (el.underline) c.fillRect(x, baseline + rule, w, rule);
    if (el.strikethrough) c.fillRect(x, baseline - el.fontH * 0.3, w, rule);
  });

  const img = c.getImageData(0, 0, widthDots, heightDots).data;
  const bytesPerRow = Math.ceil(widthDots / 8);
  const bytes = new Uint8Array(bytesPerRow * heightDots);

  for (let y = 0; y < heightDots; y++) {
    for (let x = 0; x < widthDots; x++) {
      const p = (y * widthDots + x) * 4;
      // Thresholding on luminance: a dot either burns or it does not, so anti-aliased
      // edges have to be resolved one way before they reach the printer.
      const lum = (img[p] * 299 + img[p + 1] * 587 + img[p + 2] * 114) / 1000;
      if (lum < 128) bytes[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x % 8);
    }
  }

  let hex = "";
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0").toUpperCase();

  const total = bytes.length;
  const result = {
    zpl: `^GFA,${total},${total},${bytesPerRow},${hex}`,
    widthDots,
    heightDots,
  };

  if (cache.size > CACHE_LIMIT) cache.clear();
  cache.set(key, result);
  return result;
}
